import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last-resort catch for render-time crashes. Conversion and file errors are
 * handled locally; this exists so an unexpected component failure degrades
 * into a recoverable screen instead of a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Loopsmith crashed:', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="grid min-h-dvh place-items-center bg-ink-50 p-6 dark:bg-ink-950">
        <div className="panel w-full max-w-md space-y-4 p-6 text-center">
          <p className="eyebrow">Unexpected error</p>
          <h1 className="font-display text-xl font-semibold text-ink-900 dark:text-ink-100">
            Something broke on our side
          </h1>
          <p className="text-sm text-ink-500 dark:text-ink-400">
            Your video never left this device. Reloading gives you a clean slate.
          </p>
          <p className="mx-auto max-w-full overflow-auto rounded bg-ink-100 p-2 text-left font-mono text-xs break-words text-ink-600 dark:bg-ink-800 dark:text-ink-300">
            {this.state.error.message}
          </p>
          <div className="flex justify-center gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="h-10 rounded-lg bg-signal-500 px-4 font-display text-sm font-semibold text-ink-950 hover:bg-signal-400"
            >
              Reload Loopsmith
            </button>
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="h-10 rounded-lg px-4 text-sm text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800"
            >
              Try to continue
            </button>
          </div>
        </div>
      </div>
    );
  }
}
